// 스킬 슬롯 표시 모델(순수 로직): 현재 페이지에서 어느 스킬이 몇 번째 칸에 보이는지, 쪽 표시·다음 스킬 문구·습득 알림 문구.
// DOM·three를 모른다. 규칙·저장은 shared/skillSlots.ts·game/skillSlots.ts. 정본: docs/SKILL-TREE.md 11.3.
import { learnedSkills } from '../shared/growth';
import { SKILL_FREE_MASK } from '../shared/skills/types';
import { nextSkillLevel } from '../shared/skills/unlock';
import { SLOT_PAGES, SlotState, cellOfSlot, pageOfSlot, slotOfSkill, slotsAll, type SlotsView } from '../shared/skillSlots';
import { SKILL_MAX, type GrowthView, type PlayableHeroId } from '../shared/types';
import { skillSpecOf } from './skillTreeModel';

/** 슬롯 뷰가 없는 빌드(갤러리·옛 픽스처)에서 쓰는 기본 배치: 스킬 1~5가 슬롯 1~5 */
const FALLBACK = new SlotState();
FALLBACK.place(0b11111);
export function slotsOf(view: { readonly slots?: SlotsView }): SlotsView {
  return view.slots ?? FALLBACK;
}

/**
 * 지금 쓸 수 있는 스킬 비트마스크(레벨 해금 + 트리 랭크 + 환생으로 이어 간 습득 kept). 성장 정보가 없는 빌드는 기본 3. 영웅은 장부의 hero(c022, 없으면 카엘).
 * kept(레인 R)를 빼면 환생 뒤 Lv1 스킬 바·터치 칸이 게임은 쓸 수 있는 스킬 4·5를 숨긴다(2026-10-27 실입력 QA에서 발견)
 */
export function learnedMaskOf(g: (Pick<GrowthView, 'level' | 'ranks' | 'kept'> & { readonly hero?: PlayableHeroId }) | null): number {
  return g ? learnedSkills(g.level, g.ranks, g.hero ?? 'kael', g.kept ?? 0) : SKILL_FREE_MASK;
}

/** 스킬 번호(1..5)가 현재 페이지의 몇 번째 칸(0..5)에 있는지. 다른 페이지·배치에 없으면 -1 */
export function cellOfSkill(sv: SlotsView, skill: number): number {
  const s = slotOfSkill(slotsAll(sv), skill);
  // 환생 추가 칸(레인 R)은 그 페이지의 7번째 칸(6)
  return s >= 0 && pageOfSlot(s) === sv.page ? cellOfSlot(s) : -1;
}

/** 스킬이 지금 화면에 보이는가: 습득했고 현재 페이지에 놓여 있다 */
export function skillShown(sv: SlotsView, learned: number, skill: number): boolean {
  return (learned & (1 << (skill - 1))) !== 0 && cellOfSkill(sv, skill) >= 0;
}

/** 현재 페이지 6칸의 스킬 번호(보이는 것만, 없으면 0). out을 재사용한다 */
export function pageSkills(sv: SlotsView, learned: number, out: Int8Array): Int8Array {
  out.fill(0);
  for (let id = 1; id <= SKILL_MAX; id++) {
    const c = cellOfSkill(sv, id);
    if (c >= 0 && (learned & (1 << (id - 1))) !== 0) out[c] = id;
  }
  return out;
}

/** 레이아웃이 바뀌었는지 알리는 키(슬롯 revision·습득 마스크·페이지). 같으면 DOM을 다시 쓰지 않는다 */
export function layoutKey(sv: SlotsView, learned: number): number {
  return sv.revision * 65536 + learned * 4 + sv.page;
}

export const pageText = (page: number): string => (page + 1) + '/' + SLOT_PAGES;
export const PAGE_KEY_HINT = 'Tab';

/** "다음 스킬 Lv.10" — 더 열릴 스킬이 없으면 빈 문자열(아직 없는 스킬을 약속하지 않는다) */
export function nextSkillText(level: number, hero: PlayableHeroId = 'kael'): string {
  const n = nextSkillLevel(level, hero);
  return n > 0 ? '다음 스킬 Lv.' + n : '';
}

/** 슬롯 번호(0..11) → 사용자 표시: 현재 페이지가 아니면 '2쪽 3' 꼴, 같은 페이지는 칸 번호만 */
export function slotLabel(slot: number, page: number): string {
  const n = cellOfSlot(slot) + 1;
  return pageOfSlot(slot) === page ? String(n) : (pageOfSlot(slot) + 1) + '쪽 ' + n;
}

/** 새 스킬 습득 띠 문구(Ev.SkillLearned): 머리 'NEW SKILL', 꼬리 '천공 올려베기 · 슬롯 5'. 슬롯에 없으면 이름만 */
export function skillLearnedParts(skill: number, sv: SlotsView, hero: PlayableHeroId = 'kael'): { head: string; tail: string } {
  const name = skillSpecOf(skill, hero)?.name ?? '새 스킬';
  const s = slotOfSkill(slotsAll(sv), skill);
  return { head: 'NEW SKILL', tail: s >= 0 ? name + ' · 슬롯 ' + slotLabel(s, sv.page) : name };
}
